import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  Policy as PolicyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SupervisorAccount as AdminIcon,
  Person as OwnerIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  getPendingApprovals,
  approveRequest,
  rejectRequest,
  getRequest,
  ownerApproveRequest,
  ownerRejectRequest,
  adminApproveRequest,
  adminRejectRequest,
  getOwnedAccounts,
} from '../../services/jitApi';
import { getJITUser } from '../../services/jitApi';
import { hasMinRole, ROLES } from '../../utils/roles';
import { formatPolicyJson } from '../../utils/policyJson';

const JITApprovals = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery('(max-width:767px)');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve', 'reject', 'owner-approve', 'owner-reject', 'admin-approve', 'admin-reject'
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);
  const [ownedAccounts, setOwnedAccounts] = useState([]);

  const jitUser = getJITUser();
  const isSSOAdmin = hasMinRole(jitUser?.role, ROLES.SSO_ADMIN);

  useEffect(() => {
    fetchPendingApprovals();
    fetchOwnedAccounts();
  }, []);

  const fetchPendingApprovals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPendingApprovals();
      setPendingRequests(response.data || []);
    } catch (err) {
      console.error('Failed to fetch pending approvals:', err);
      setError('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnedAccounts = async () => {
    try {
      const response = await getOwnedAccounts();
      setOwnedAccounts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch owned accounts:', err);
    }
  };

  const handleRefresh = () => {
    fetchPendingApprovals();
  };

  const handleViewDetails = async (requestId) => {
    try {
      const response = await getRequest(requestId);
      setSelectedRequest(response.data);
      setDetailsOpen(true);
      setShowPolicies(false);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
    }
  };

  const openActionDialog = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setComment('');
    setActionDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;

    setProcessing(true);
    try {
      let response;
      switch (actionType) {
        case 'approve':
          await approveRequest(selectedRequest.id, comment);
          toast.success('Request approved successfully');
          break;
        case 'reject':
          await rejectRequest(selectedRequest.id, comment);
          toast.success('Request rejected');
          break;
        case 'owner-approve':
          // Owner approval may fully complete if owner is also SSO admin
          response = await ownerApproveRequest(selectedRequest.id, comment);
          // Use message from response - it will indicate if fully approved or awaiting admin
          toast.success(response.message || 'Owner approval recorded');
          break;
        case 'owner-reject':
          await ownerRejectRequest(selectedRequest.id, comment);
          toast.success('Request rejected');
          break;
        case 'admin-approve':
          await adminApproveRequest(selectedRequest.id, comment);
          toast.success('Request fully approved - access granted');
          break;
        case 'admin-reject':
          await adminRejectRequest(selectedRequest.id, comment);
          toast.success('Request rejected');
          break;
        default:
          throw new Error('Unknown action type');
      }
      setActionDialogOpen(false);
      handleRefresh();
    } catch (err) {
      console.error(`Failed to ${actionType} request:`, err);
      toast.error(err.message || `Failed to ${actionType} request`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status, isCustomPS) => {
    switch (status) {
      case 'pending_owner':
        return (
          <Chip
            size="small"
            label="Awaiting Owner"
            icon={<OwnerIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#E65100',
              fontWeight: 500,
            }}
          />
        );
      case 'pending_admin':
        return (
          <Chip
            size="small"
            label="Awaiting Admin"
            icon={<AdminIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(70, 152, 211, 0.15)',
              color: '#1565C0',
              fontWeight: 500,
            }}
          />
        );
      case 'pending':
        return (
          <Chip
            size="small"
            label="Pending"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(156, 39, 176, 0.15)',
              color: '#7B1FA2',
              fontWeight: 500,
            }}
          />
        );
      default:
        return (
          <Chip
            size="small"
            label={status}
            sx={{ fontFamily: 'Metropolis, sans-serif' }}
          />
        );
    }
  };

  const getActionButtons = (request) => {
    const buttons = [];

    // View details button (always shown)
    buttons.push(
      <Tooltip key="details" title="View Details">
        <IconButton
          size="small"
          onClick={() => handleViewDetails(request.id)}
          sx={{
            color: '#4698D3',
            '&:hover': {
              backgroundColor: 'rgba(70, 152, 211, 0.08)',
            },
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    if (request.isCustomPermissionSet) {
      // Multi-level approval buttons
      if (request.status === 'pending_owner') {
        // Owner approval buttons (shown in main view, not admin view)
        buttons.push(
          <Tooltip key="owner-approve" title={isSSOAdmin ? 'Approve (Final)' : 'Owner Approve'}>
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'owner-approve')}
              sx={{
                color: '#27AE60',
                '&:hover': { backgroundColor: 'rgba(39, 174, 96, 0.08)' },
              }}
            >
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
        buttons.push(
          <Tooltip key="owner-reject" title="Reject">
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'owner-reject')}
              sx={{
                color: '#E74C3C',
                '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.08)' },
              }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      } else if (request.status === 'pending_admin' && isSSOAdmin) {
        // Admin approval buttons (shown to SSO admins regardless of view)
        buttons.push(
          <Tooltip key="admin-approve" title="Admin Approve (Final)">
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'admin-approve')}
              sx={{
                color: '#27AE60',
                '&:hover': { backgroundColor: 'rgba(39, 174, 96, 0.08)' },
              }}
            >
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
        buttons.push(
          <Tooltip key="admin-reject" title="Reject">
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'admin-reject')}
              sx={{
                color: '#E74C3C',
                '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.08)' },
              }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    } else {
      // Standard single-level approval
      if (request.status === 'pending') {
        buttons.push(
          <Tooltip key="approve" title="Approve">
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'approve')}
              sx={{
                color: '#27AE60',
                '&:hover': { backgroundColor: 'rgba(39, 174, 96, 0.08)' },
              }}
            >
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
        buttons.push(
          <Tooltip key="reject" title="Reject">
            <IconButton
              size="small"
              onClick={() => openActionDialog(request, 'reject')}
              sx={{
                color: '#E74C3C',
                '&:hover': { backgroundColor: 'rgba(231, 76, 60, 0.08)' },
              }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
    }

    return buttons;
  };

  const getActionDialogTitle = () => {
    switch (actionType) {
      case 'approve':
        return 'Approve Request';
      case 'reject':
        return 'Reject Request';
      case 'owner-approve':
        return isSSOAdmin ? 'Approve Request (Final)' : 'Owner Approval';
      case 'owner-reject':
        return 'Reject Request (Owner)';
      case 'admin-approve':
        return 'Admin Approval (Final)';
      case 'admin-reject':
        return 'Reject Request (Admin)';
      default:
        return 'Confirm Action';
    }
  };

  const getActionDialogDescription = () => {
    switch (actionType) {
      case 'approve':
        return 'This will grant the requester temporary access to the specified AWS account.';
      case 'owner-approve':
        return isSSOAdmin
          ? 'As an SSO admin, your approval covers both the owner and admin levels. The custom permission set will be created and the requester will be granted access.'
          : 'As the account owner, you are approving this request. It will then proceed to SSO admin for final approval.';
      case 'admin-approve':
        return 'This is the final approval. The custom permission set will be created and the requester will be granted access.';
      case 'reject':
      case 'owner-reject':
      case 'admin-reject':
        return 'This will reject the access request. The requester will be notified.';
      default:
        return '';
    }
  };

  const renderApprovalTimeline = (request) => {
    if (!request.isCustomPermissionSet) return null;

    const steps = [
      {
        label: 'Request Submitted',
        completed: true,
        description: formatDate(request.createdAt),
      },
      {
        label: 'Owner Approval',
        completed: !!request.ownerApprovedAt,
        description: request.ownerApprovedAt
          ? `${request.ownerApproverEmail} on ${formatDate(request.ownerApprovedAt)}`
          : 'Pending',
      },
      {
        label: 'Admin Approval',
        completed: !!request.adminApprovedAt,
        description: request.adminApprovedAt
          ? `${request.adminApproverEmail} on ${formatDate(request.adminApprovedAt)}`
          : 'Pending',
      },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            color: '#253E66',
            mb: 2,
          }}
        >
          Approval Progress
        </Typography>
        <Stepper orientation="vertical" activeStep={steps.findIndex(s => !s.completed)}>
          {steps.map((step, index) => (
            <Step key={step.label} completed={step.completed}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    color: step.completed ? '#27AE60' : undefined,
                    '&.Mui-completed': { color: '#27AE60' },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: step.completed ? 600 : 400,
                  }}
                >
                  {step.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#666666',
                  }}
                >
                  {step.description}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  };

  const renderCustomPSDetails = (request) => {
    if (!request.isCustomPermissionSet) return null;

    const managedPolicies = request.customManagedPolicies || [];
    const inlinePolicies = request.customInlinePolicies || {};

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 1,
          }}
          onClick={() => setShowPolicies(!showPolicies)}
        >
          {showPolicies ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              ml: 1,
            }}
          >
            Custom Permission Set Details
          </Typography>
          <Chip
            size="small"
            icon={<PolicyIcon sx={{ fontSize: 12 }} />}
            label="Custom"
            sx={{
              ml: 1,
              fontFamily: 'Metropolis, sans-serif',
              backgroundColor: 'rgba(255, 152, 0, 0.15)',
              color: '#E65100',
              height: 22,
            }}
          />
        </Box>

        <Collapse in={showPolicies}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.04)',
              border: '1px solid rgba(255, 152, 0, 0.15)',
              borderRadius: '6px',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontFamily: 'Metropolis, sans-serif' }}
              >
                Permission Set Name
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
              >
                JIT-{request.customPermissionSetName}
              </Typography>
            </Box>

            {managedPolicies.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontFamily: 'Metropolis, sans-serif' }}
                >
                  AWS Managed Policies ({managedPolicies.length})
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {managedPolicies.map((arn, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#333',
                        backgroundColor: '#f5f5f5',
                        p: 0.5,
                        borderRadius: '4px',
                        mb: 0.5,
                        wordBreak: 'break-all',
                      }}
                    >
                      {arn}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {Object.keys(inlinePolicies).length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontFamily: 'Metropolis, sans-serif' }}
                >
                  Inline Policies ({Object.keys(inlinePolicies).length})
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(inlinePolicies).map(([name, document], idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                      >
                        {name}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={formatPolicyJson(document)}
                        InputProps={{ readOnly: true }}
                        sx={{
                          mt: 0.5,
                          '& .MuiInputBase-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Collapse>
      </Box>
    );
  };

  const renderRequestsTable = (requests) => {
    if (requests.length === 0) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid #E5E5E5',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(39, 174, 96, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 2,
            }}
          >
            <ApproveIcon sx={{ fontSize: 32, color: '#27AE60' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            No Pending Approvals
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            You're all caught up! There are no requests waiting for your approval.
          </Typography>
        </Paper>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {requests.map((request, index) => (
            <Paper
              key={request.id}
              elevation={0}
              onClick={() => handleViewDetails(request.id)}
              sx={{
                '@keyframes fadeInItem': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                p: 2,
                border: '1px solid #E5E5E5',
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4698D3',
                  backgroundColor: 'rgba(70, 152, 211, 0.02)',
                },
                '&:active': {
                  backgroundColor: 'rgba(70, 152, 211, 0.06)',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  color: '#253E66',
                  mb: 1.5,
                }}
              >
                {request.requesterEmail}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Account
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {request.accountName || request.accountId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', flexShrink: 0 }}
                >
                  Permission Set
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    color: '#333333',
                    ml: 2,
                    textAlign: 'right',
                  }}
                >
                  {request.isCustomPermissionSet
                    ? `JIT-${request.customPermissionSetName}`
                    : (request.permissionSetName || request.permissionSetId)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 1.5,
                  borderTop: '1px solid #F0F0F0',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {getStatusBadge(request.status, request.isCustomPermissionSet)}
                  <Chip
                    size="small"
                    icon={<TimeIcon sx={{ fontSize: 14 }} />}
                    label={`${request.requestedDuration}h`}
                    sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}
                  />
                </Box>
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: 'flex', gap: 0.5 }}
                >
                  {getActionButtons(request)}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflowX: 'auto',
        }}
      >
        <Table sx={{ minWidth: { xs: 500, sm: 800 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Requester
              </TableCell>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Account
              </TableCell>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Permission Set
              </TableCell>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Status
              </TableCell>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Duration
              </TableCell>
              <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Requested
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request, index) => (
              <TableRow
                key={request.id}
                hover
                onClick={() => handleViewDetails(request.id)}
                sx={{
                  '@keyframes fadeInRow': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(70, 152, 211, 0.02)',
                  },
                }}
              >
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      color: '#253E66',
                    }}
                  >
                    {request.requesterEmail}
                  </Typography>
                  {request.requesterName && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666666',
                        fontFamily: 'Metropolis, sans-serif',
                      }}
                    >
                      {request.requesterName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      color: '#333333',
                    }}
                  >
                    {request.accountName || request.accountId}
                  </Typography>
                  {request.accountName && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#666666',
                        fontFamily: 'Metropolis, sans-serif',
                      }}
                    >
                      {request.accountId}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#333333',
                      }}
                    >
                      {request.isCustomPermissionSet
                        ? `JIT-${request.customPermissionSetName}`
                        : (request.permissionSetName || request.permissionSetId)}
                    </Typography>
                    {request.isCustomPermissionSet && (
                      <Chip
                        size="small"
                        label="Custom"
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          backgroundColor: 'rgba(255, 152, 0, 0.15)',
                          color: '#E65100',
                          height: 20,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {getStatusBadge(request.status, request.isCustomPermissionSet)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={<TimeIcon sx={{ fontSize: 14 }} />}
                    label={`${request.requestedDuration}h`}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      color: '#666666',
                    }}
                  >
                    {formatDate(request.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {getActionButtons(request)}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              mb: 1,
            }}
          >
            Pending Approvals
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
            }}
          >
            Review and approve access requests
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={handleRefresh}
            sx={{
              border: '1px solid #E5E5E5',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#4698D3',
                backgroundColor: 'rgba(70, 152, 211, 0.04)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Pending approvals table */}
      <motion.div variants={sectionVariants}>
      {renderRequestsTable(pendingRequests)}
      </motion.div>

      {/* Request Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
          Request Details
        </DialogTitle>
        <DialogContent dividers>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Requester
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, wordBreak: 'break-word' }}>
                    {selectedRequest.requesterEmail}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusBadge(selectedRequest.status, selectedRequest.isCustomPermissionSet)}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    AWS Account
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.accountName || selectedRequest.accountId}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    Duration
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.requestedDuration} hours
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Permission Set
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                    {selectedRequest.isCustomPermissionSet
                      ? `JIT-${selectedRequest.customPermissionSetName}`
                      : (selectedRequest.permissionSetName || selectedRequest.permissionSetId)}
                  </Typography>
                  {selectedRequest.isCustomPermissionSet && (
                    <Chip
                      size="small"
                      icon={<PolicyIcon sx={{ fontSize: 14 }} />}
                      label="Custom Permission Set"
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        color: '#E65100',
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Justification
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 0.5,
                    backgroundColor: '#F9FBFD',
                    border: '1px solid #E5E5E5',
                    borderRadius: '6px',
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedRequest.justification}
                  </Typography>
                </Paper>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  Requested At
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                  {formatDate(selectedRequest.createdAt)}
                </Typography>
              </Box>

              {/* Custom Permission Set Details */}
              {renderCustomPSDetails(selectedRequest)}

              {/* Approval Timeline */}
              {renderApprovalTimeline(selectedRequest)}
            </Box>
          )}
          </motion.div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          {selectedRequest && (
            <>
              {/* Show appropriate action buttons based on status */}
              {selectedRequest.isCustomPermissionSet ? (
                <>
                  {selectedRequest.status === 'pending_owner' && (
                    <>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setDetailsOpen(false);
                          openActionDialog(selectedRequest, 'owner-reject');
                        }}
                        sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setDetailsOpen(false);
                          openActionDialog(selectedRequest, 'owner-approve');
                        }}
                        sx={{
                          backgroundColor: '#27AE60',
                          fontFamily: 'Metropolis, sans-serif',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#219653' },
                        }}
                      >
                        {isSSOAdmin ? 'Approve (Final)' : 'Owner Approve'}
                      </Button>
                    </>
                  )}
                  {selectedRequest.status === 'pending_admin' && isSSOAdmin && (
                    <>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setDetailsOpen(false);
                          openActionDialog(selectedRequest, 'admin-reject');
                        }}
                        sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setDetailsOpen(false);
                          openActionDialog(selectedRequest, 'admin-approve');
                        }}
                        sx={{
                          backgroundColor: '#27AE60',
                          fontFamily: 'Metropolis, sans-serif',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#219653' },
                        }}
                      >
                        Admin Approve (Final)
                      </Button>
                    </>
                  )}
                </>
              ) : (
                selectedRequest.status === 'pending' && (
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setDetailsOpen(false);
                        openActionDialog(selectedRequest, 'reject');
                      }}
                      sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setDetailsOpen(false);
                        openActionDialog(selectedRequest, 'approve');
                      }}
                      sx={{
                        backgroundColor: '#27AE60',
                        fontFamily: 'Metropolis, sans-serif',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#219653' },
                      }}
                    >
                      Approve
                    </Button>
                  </>
                )
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !processing && setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
          {getActionDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              color: '#666666',
              mb: 2,
            }}
          >
            {getActionDialogDescription()}
          </Typography>
          {selectedRequest && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: selectedRequest.isCustomPermissionSet
                  ? 'rgba(255, 152, 0, 0.04)'
                  : '#F9FBFD',
                border: selectedRequest.isCustomPermissionSet
                  ? '1px solid rgba(255, 152, 0, 0.15)'
                  : '1px solid #E5E5E5',
                borderRadius: '6px',
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                Request Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip size="small" label={selectedRequest.requesterEmail} />
                <Chip size="small" label={selectedRequest.accountId} />
                <Chip size="small" label={`${selectedRequest.requestedDuration}h`} />
                {selectedRequest.isCustomPermissionSet && (
                  <Chip
                    size="small"
                    label="Custom PS"
                    sx={{
                      backgroundColor: 'rgba(255, 152, 0, 0.15)',
                      color: '#E65100',
                    }}
                  />
                )}
              </Box>
            </Paper>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comment (optional)"
            placeholder={
              actionType?.includes('approve')
                ? 'Add any notes for the requester...'
                : 'Provide a reason for rejection...'
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'Metropolis, sans-serif',
              },
            }}
          />
          </motion.div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialogOpen(false)}
            disabled={processing}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAction}
            disabled={processing}
            sx={{
              backgroundColor: actionType?.includes('reject') ? '#E74C3C' : '#27AE60',
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: actionType?.includes('reject') ? '#C0392B' : '#219653',
              },
            }}
          >
            {processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : actionType?.includes('approve') ? (
              actionType === 'admin-approve' || (actionType === 'owner-approve' && isSSOAdmin) ? 'Approve (Final)' : 'Approve'
            ) : (
              'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default JITApprovals;
